import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../Utils/dateFormatter";

const InvoiceDetail = ({ invoice }) => {
    const [expanded, setExpanded] = useState(
        Number(invoice.remainingAmount) > 0,
    );
    console.log("Invoice Detail:", invoice);
    const navigate = useNavigate();

    const isUnpaid = Number(invoice.remainingAmount) > 0;
    const statusColor = isUnpaid ? "error" : "success";
    const statusText = isUnpaid ? "Unpaid" : "Paid";

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            className={`invoice-detail-container ${
                isUnpaid ? "unpaid-invoice" : "paid-invoice"
            }`}
            sx={{ mb: 2 }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`invoice-${invoice.id}-content`}
                id={`invoice-${invoice.id}-header`}
            >
                {" "}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        alignItems: "center",
                    }}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Invoice #{invoice.id} -{" "}
                            {formatDate(invoice.createdAt)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Sold by: {invoice.employee?.name || "Unknown"}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                            sx={{
                                mr: 3,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                            }}
                        >
                            <Chip
                                label={statusText}
                                color={statusColor}
                                size="small"
                                sx={{ mb: 1, fontWeight: "bold" }}
                            />{" "}
                            <Box
                                sx={{
                                    mt: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-end",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "medium" }}
                                >
                                    <strong>Total:</strong>{" "}
                                    {Number(invoice.priceFinal).toFixed(4)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "medium" }}
                                >
                                    <strong>Paid:</strong>{" "}
                                    {Number(invoice.paidAmount).toFixed(4)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: "bold",
                                        color: isUnpaid ? "#d32f2f" : "#2e7d32",
                                    }}
                                >
                                    <strong>Remaining:</strong>{" "}
                                    {Number(invoice.remainingAmount).toFixed(4)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                {isUnpaid && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mb: 2,
                        }}
                        className="noprint"
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => navigate(`/Invoice/${invoice.id}`)}
                        >
                            Process Payment
                        </Button>
                    </Box>
                )}{" "}
                <Box
                    sx={{
                        mb: 3,
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <Box>
                        <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            gutterBottom
                        >
                            Invoice Summary
                        </Typography>
                        <Typography variant="body2">
                            Invoice #{invoice.id} | Date:{" "}
                            {formatDate(invoice.createdAt)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Sold by: {invoice.employee?.name || "Unknown"}
                        </Typography>
                    </Box>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 2,
                            bgcolor: isUnpaid ? "#fff8f8" : "#f8fff8",
                            border: "1px solid",
                            borderColor: isUnpaid ? "#ffcdd2" : "#c8e6c9",
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            gutterBottom
                        >
                            Payment Status
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                            }}
                        >
                            <Typography variant="body2" sx={{ mr: 2 }}>
                                Total:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                {Number(invoice.priceFinal).toFixed(4)}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                            }}
                        >
                            <Typography variant="body2" sx={{ mr: 2 }}>
                                Paid:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                {Number(invoice.paidAmount).toFixed(4)}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography variant="body2" sx={{ mr: 2 }}>
                                Remaining:
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={isUnpaid ? "error.main" : "success.main"}
                            >
                                {Number(invoice.remainingAmount).toFixed(4)}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
                {invoice.InvoiceItem && invoice.InvoiceItem.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Quantity</TableCell>
                                    <TableCell align="right">
                                        Unit Price
                                    </TableCell>
                                    <TableCell align="right">Taxable</TableCell>
                                    <TableCell align="right">
                                        Discount %
                                    </TableCell>
                                    <TableCell align="right">
                                        Discount $
                                    </TableCell>
                                    <TableCell align="right">Tax</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoice.InvoiceItem.map((item, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            {item.item?.name || "Unknown Item"}
                                        </TableCell>
                                        <TableCell>{item.quantity}</TableCell>
                                        <TableCell align="right">
                                            {Number(item.price).toFixed(4)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Checkbox
                                                checked={item.isTaxable}
                                                disabled
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(
                                                item.discountPercentage,
                                            ).toFixed(2)}
                                            %
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(
                                                item.discountAmount,
                                            ).toFixed(4)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(item.taxAmount).toFixed(4)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(
                                                item.quantity * item.price -
                                                    item.discountAmount +
                                                    item.taxAmount,
                                            ).toFixed(4)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={6} />
                                    <TableCell align="right">
                                        <strong>Subtotal:</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        {Number(invoice.priceFirst).toFixed(4)}
                                    </TableCell>
                                </TableRow>
                                {Number(invoice.discountPercentage) > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} />
                                        <TableCell align="right">
                                            <strong>
                                                Discount (
                                                {Number(
                                                    invoice.discountPercentage,
                                                ).toFixed(4)}
                                                %):
                                            </strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            -
                                            {Number(
                                                invoice.discountAmount,
                                            ).toFixed(4)}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {Number(invoice.taxPercentage) > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} />
                                        <TableCell align="right">
                                            <strong>Tax:</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(invoice.priceTax).toFixed(
                                                2,
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell colSpan={6} />
                                    <TableCell align="right">
                                        <strong>Total:</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        {Number(invoice.priceFinal).toFixed(4)}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={6} />
                                    <TableCell align="right">
                                        <strong>Paid:</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        {Number(invoice.paidAmount).toFixed(4)}
                                    </TableCell>
                                </TableRow>
                                <TableRow
                                    className={isUnpaid ? "remaining-row" : ""}
                                >
                                    <TableCell colSpan={6} />
                                    <TableCell align="right">
                                        <strong>Remaining:</strong>
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        className={
                                            isUnpaid
                                                ? "remaining-amount-cell"
                                                : ""
                                        }
                                    >
                                        {Number(
                                            invoice.remainingAmount,
                                        ).toFixed(4)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography variant="body2">
                        No items found for this invoice.
                    </Typography>
                )}{" "}
                {/* Payment history for this invoice */}
                {invoice.Payment && invoice.Payment.length > 0 ? (
                    <Box sx={{ mt: 4 }}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 2,
                                bgcolor: "#fafafa",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                gutterBottom
                                sx={{
                                    borderBottom: "2px solid #2196f3",
                                    pb: 1,
                                    display: "inline-block",
                                }}
                            >
                                Payment History
                            </Typography>

                            <TableContainer sx={{ mt: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow
                                            sx={{ backgroundColor: "#f5f5f5" }}
                                        >
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                Payment Date
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                Amount
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                Received By
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice.Payment.map(
                                            (payment, paymentIdx) => (
                                                <TableRow
                                                    key={paymentIdx}
                                                    hover
                                                >
                                                    <TableCell>
                                                        {formatDate(
                                                            payment.createdAt,
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            fontWeight:
                                                                "medium",
                                                            color: "#2e7d32",
                                                        }}
                                                    >
                                                        {Number(
                                                            payment.amount,
                                                        ).toFixed(4)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.employee
                                                            ?.name || "Unknown"}
                                                    </TableCell>
                                                </TableRow>
                                            ),
                                        )}
                                        <TableRow
                                            sx={{ backgroundColor: "#f5f5f5" }}
                                        >
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                Total Paid
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                {Number(
                                                    invoice.paidAmount,
                                                ).toFixed(4)}
                                            </TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                ) : (
                    Number(invoice.paidAmount) > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                fontStyle="italic"
                            >
                                This invoice has{" "}
                                {Number(invoice.paidAmount).toFixed(4)} in
                                payments, but no detailed payment history is
                                available.
                            </Typography>
                        </Box>
                    )
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default InvoiceDetail;
